import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BarChart3, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { CANALI_VENDITA, fmtEUR, fmtNum, fmtPct, nowPeriod, parseNum, round2, toInput, ymStr } from "@/lib/format";
import { EmptyState, Hint, Loading, PageHeader, PeriodSelect, btnPrimary } from "@/components/app/common";
import { useAuth } from "@/context/AuthContext";
import { MarcaSelect, useMarche } from "@/components/app/MarcaSelect";

const cellInput = "w-full h-9 rounded-lg border border-[#EAE3DC] bg-white px-2 text-right tabular text-sm focus:outline-none focus:ring-2 focus:ring-[#8C6D62]/30";

function rowCalc(row, edits) {
  let ricavo = 0, cogs = 0, qtyTot = 0;
  for (const c of CANALI_VENDITA) {
    const base = row.canali[c];
    const e = edits[`${row.prodotto_id}|${c}`];
    const qty = e ? parseInt(e.quantita || "0", 10) || 0 : base.quantita;
    const pm = e ? parseNum(e.prezzo_medio) : base.prezzo_medio;
    const prezzo = pm ?? base.prezzo_listino;
    ricavo += qty * prezzo;
    cogs += qty * (base.costo_unitario || 0);
    qtyTot += qty;
  }
  return { ricavo: round2(ricavo), cogs: round2(cogs), margine: round2(ricavo - cogs), qty: qtyTot };
}

export default function Vendite() {
  const { isAdmin } = useAuth();
  const [period, setPeriod] = useState(nowPeriod());
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [marca, setMarca] = useState("all");
  const marche = useMarche();
  const mese = ymStr(period.anno, period.mese || 1);

  const load = useCallback(() => {
    setData(null);
    api.get("/vendite", { params: { mese } }).then((r) => { setData(r.data); setEdits({}); }).catch((e) => toast.error(errMsg(e)));
  }, [mese]);
  useEffect(load, [load]);

  const setCell = (row, c, field, value) => {
    const key = `${row.prodotto_id}|${c}`;
    setEdits((prev) => {
      const base = prev[key] || { quantita: String(row.canali[c].quantita || ""), prezzo_medio: toInput(row.canali[c].prezzo_medio) };
      return { ...prev, [key]: { ...base, [field]: value } };
    });
  };

  const view = useMemo(() => data && { ...data, righe: data.righe.filter((r) => marca === "all" || r.marca_id === marca) }, [data, marca]);
  const totals = useMemo(() => {
    if (!view) return null;
    return view.righe.reduce((t, r) => { const x = rowCalc(r, edits); return { ricavo: t.ricavo + x.ricavo, cogs: t.cogs + x.cogs, qty: t.qty + x.qty }; }, { ricavo: 0, cogs: 0, qty: 0 });
  }, [view, edits]);

  const dirty = Object.keys(edits).length;

  const save = async () => {
    const righe = [];
    for (const [key, e] of Object.entries(edits)) {
      const [prodotto_id, canale] = key.split("|");
      const quantita = parseInt(e.quantita || "0", 10);
      const pm = parseNum(e.prezzo_medio);
      if (!(quantita >= 0) || (pm != null && pm < 0)) return toast.error("Controlla quantità e prezzi: devono essere numeri positivi");
      righe.push({ prodotto_id, canale, quantita, prezzo_medio: pm });
    }
    setSaving(true);
    try { await api.put("/vendite", { mese, righe }); toast.success("Vendite del mese salvate"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="vendite-page">
      <PageHeader overline="Analisi prodotti" title="Vendite mensili" subtitle="Inserisci i pezzi venduti per ogni prodotto e canale. Ricavo e margine si calcolano da soli.">
        <PeriodSelect anno={period.anno} mese={period.mese || 1} onChange={(p) => setPeriod({ ...p, mese: p.mese || 1 })} prefix="vendite" />
        <MarcaSelect marche={marche} value={marca} onChange={setMarca} testId="vendite-marca-select" />
        <Button className={btnPrimary} onClick={save} disabled={!dirty || saving} data-testid="save-vendite-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Salva{dirty ? ` (${dirty})` : ""}</>}
        </Button>
      </PageHeader>
      <div className="mb-6">
        <Hint testId="vendite-hint">Queste quantità servono <b>solo per l'analisi dei margini per prodotto</b>. Il fatturato della dashboard deriva invece dagli incassi registrati nei Movimenti: così non c'è doppio conteggio. Il prezzo medio è facoltativo (es. promozioni): se vuoto si usa il prezzo di listino.</Hint>
      </div>
      {!data ? <Loading /> : !data.righe.length ? (
        <EmptyState icon={BarChart3} title="Nessun prodotto attivo" text="Aggiungi prodotti al catalogo per registrare le vendite mensili." />
      ) : (
        <GridTable data={view} edits={edits} setCell={setCell} isAdmin={isAdmin} totals={totals} />
      )}
    </div>
  );
}

function GridTable({ data, edits, setCell, isAdmin, totals }) {
  const val = (row, c, f) => {
    const e = edits[`${row.prodotto_id}|${c}`];
    if (e) return e[f];
    return f === "quantita" ? (row.canali[c].quantita ? String(row.canali[c].quantita) : "") : toInput(row.canali[c].prezzo_medio);
  };
  const tm = totals.ricavo - totals.cogs;
  return (
    <div className="card-soft overflow-x-auto" data-testid="vendite-grid">
      <table className="w-full text-sm min-w-[940px]">
        <thead className="bg-[#FAF7F5] text-[11px] uppercase tracking-wider text-[#756B64]">
          <tr>
            <th rowSpan={2} className="text-left font-semibold px-4 py-3">Prodotto</th>
            {CANALI_VENDITA.map((c) => <th key={c} colSpan={2} className={`font-semibold px-3 pt-3 pb-1 text-center ${c === "E-commerce" ? "text-[#4E6656]" : "text-[#9A5440]"}`}>{c}</th>)}
            <th rowSpan={2} className="text-right font-semibold px-4 py-3">Ricavo</th>
            {isAdmin && <th rowSpan={2} className="text-right font-semibold px-4 py-3">Margine €</th>}
            {isAdmin && <th rowSpan={2} className="text-right font-semibold px-4 py-3">Margine %</th>}
          </tr>
          <tr>{CANALI_VENDITA.map((c) => [<th key={`${c}q`} className="font-medium px-3 pb-3 text-right normal-case tracking-normal">Q.tà</th>, <th key={`${c}p`} className="font-medium px-3 pb-3 text-right normal-case tracking-normal">Prezzo medio</th>])}</tr>
        </thead>
        <tbody className="divide-y divide-[#EAE3DC]">
          {data.righe.map((row, i) => {
            const x = rowCalc(row, edits);
            return (
              <tr key={row.prodotto_id} data-testid={`vendite-row-${i}`} className="hover:bg-[#FAF7F5]/70">
                <td className="px-4 py-2.5">
                  <div className="font-medium leading-tight">{row.nome}</div>
                  <div className="text-[11px] text-[#756B64]"><b className="font-semibold text-[#5E4A42]">{row.marca || "Senza marca"}</b> · {row.linea} · listino {fmtEUR(row.canali["E-commerce"].prezzo_listino)}</div>
                </td>
                {CANALI_VENDITA.map((c) => {
                  const k = c === "E-commerce" ? "ec" : "rf";
                  return [
                    <td key={`${c}q`} className="px-2 py-2 w-24"><input data-testid={`qty-${k}-${i}`} inputMode="numeric" className={cellInput} value={val(row, c, "quantita")} placeholder="0" onChange={(e) => setCell(row, c, "quantita", e.target.value.replace(/[^\d]/g, ""))} /></td>,
                    <td key={`${c}p`} className="px-2 py-2 w-28"><input data-testid={`price-${k}-${i}`} inputMode="decimal" className={cellInput} value={val(row, c, "prezzo_medio")} placeholder={fmtNum(row.canali[c].prezzo_listino)} onChange={(e) => setCell(row, c, "prezzo_medio", e.target.value)} /></td>,
                  ];
                })}
                <td className="px-4 py-2.5 text-right tabular font-medium" data-testid={`ricavo-${i}`}>{fmtEUR(x.ricavo)}</td>
                {isAdmin && <td className={`px-4 py-2.5 text-right tabular font-semibold ${x.margine < 0 ? "text-[#B4553D]" : "text-[#4E6656]"}`} data-testid={`margine-${i}`}>{fmtEUR(x.margine)}</td>}
                {isAdmin && <td className="px-4 py-2.5 text-right tabular text-[#756B64]">{fmtPct(x.ricavo ? (x.margine / x.ricavo) * 100 : null)}</td>}
              </tr>
            );
          })}
        </tbody>
        <tfoot className="bg-[#F4EFEA] font-semibold">
          <tr data-testid="vendite-totali">
            <td className="px-4 py-3">Totale mese · {totals.qty} pezzi</td>
            <td colSpan={4} />
            <td className="px-4 py-3 text-right tabular" data-testid="vendite-totale-ricavo">{fmtEUR(totals.ricavo)}</td>
            {isAdmin && <td className="px-4 py-3 text-right tabular" data-testid="vendite-totale-margine">{fmtEUR(tm)}</td>}
            {isAdmin && <td className="px-4 py-3 text-right tabular">{fmtPct(totals.ricavo ? (tm / totals.ricavo) * 100 : null)}</td>}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
